/* eslint-disable react/jsx-curly-newline */
import CTNBadge from '@ctn/ui/components/ctn-badge';
import { CTNIcon } from '@ctn/ui/components/ctn-icon';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import ActionPopup, { type ActionPopupRef, type ItemsActionPopup } from '@/components/ActionsPopup';
import { UserMini } from '@/components/UserMini';
import { useDirtyForm } from '@/context/dirtyForm.context';
import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import { getLogout } from '@/services/login.service';

import { ROUTES, ROUTES_LABEL } from '@/constants/routes.constants';
import { TIPO_USUARIO } from '@/constants/tramite.constants';
import { notificationsConfig } from '@/helpers/notifications';

import { LogoPNPrivate } from './parts';

type HeadProps = {
	readonly toggleMenu: () => void;
};

type ActiveButton = 'HOME' | 'MENSAJES' | 'ADMIN';

export default function HeadPrivate({ toggleMenu }: HeadProps): React.ReactElement {
	const { numMessages, updateMessages } = usePageContext();
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const {
		user,
		clean: cleanGlobalState,
		maestrosGeneric: { rol },
	} = useGlobalContext();
	const navigate = useNavigate();
	const location = useLocation();
	const { checkDirtyForm } = useDirtyForm();
	const [activeButton, setActiveButton] = useState<ActiveButton>();
	const intervaloRef = useRef<NodeJS.Timeout | undefined>(undefined);
	const actionPopupRef = useRef<ActionPopupRef>(null);
	const useMiniRef = useRef<HTMLButtonElement>(null);

	const confirmarCerrar = async () => {
		const { code, data } = await getLogout(user?.username as string);
		if (code === 200) {
			window.location.replace(data);
			sessionStorage.clear();
			cleanGlobalState();
		}
	};

	const cerrarSession = async () => {
		openNotification(
			notificationTemplate.removeItem({
				title: 'Aviso',
				content: '¿Estás seguro que desea salir del Portal Notarial de la Empresa y Profesionales?',
				onAccept() {
					confirmarCerrar();
				},
			}),
		);
	};

	useEffect(() => {
		if (user) {
			const PATH_TO_BUTTON_MAP: Record<string, ActiveButton | undefined> = {
				[ROUTES.HOME]: 'HOME',
				[ROUTES.MENSAJES]: 'MENSAJES',
				none: undefined,
			};

			const foundSegment = Object.keys(PATH_TO_BUTTON_MAP).find((segment) => location.pathname.includes(segment));

			setActiveButton(PATH_TO_BUTTON_MAP[foundSegment ?? 'none']);
		}
	}, [location.pathname, user]);

	useEffect(() => {
		updateMessages();

		const iniciarIntervalo = () => {
			if (intervaloRef.current) clearInterval(intervaloRef.current);
			intervaloRef.current = setInterval(() => {
				updateMessages();
			}, 240000); // 4 min
		};

		iniciarIntervalo();

		return () => {
			if (intervaloRef.current) {
				clearInterval(intervaloRef.current);
				intervaloRef.current = undefined;
			}
		};
	}, [location.pathname, updateMessages]);

	const showSocios = 
    user?.nifEmisor.toLowerCase() === 'S4878368B'.toLowerCase() || 
    user?.nifEmisor.toLowerCase() === 'A78315348'.toLowerCase(); 

	const ITEMS: ItemsActionPopup[] = [
		{ icon: 'logout', label: 'Cerrar sesión', command() {
			cerrarSession(); 
		} }
	];

	if (rol?.find((opt) => opt.id === user?.idRol)?.id === TIPO_USUARIO.ADMIN ) {
		ITEMS.unshift({ 
			icon: 'config', 
			label: ROUTES_LABEL.ADMINISTRAR, 
			command() {
				checkDirtyForm({
					action() {
						navigate(ROUTES.ADMINISTRAR);
					},
				});
			} 
		});
	}

	if (showSocios) {
		ITEMS.unshift({ 
			icon: 'bookProtocol', 
			label: ROUTES_LABEL.LIBRO_SOCIOS, 
			command() {
				navigate(ROUTES.LIBRO_SOCIOS); 
			}
		});
	}

	return (
		<div className="o-layout__header">
			<header className="c-pnp-header">
				<button type="button" className="c-pnp-header__hamb" onClick={toggleMenu}>
					<CTNIcon className="c-pnp-header__hamb-icon" icon="hamburgerMini" color="blue-80" />
				</button>
				<h1 className="c-pnp-header__logo">
					<LogoPNPrivate
						onClick={() => {
							checkDirtyForm({
								action() {
									navigate(ROUTES.HOME);
								},
							});
						}}
					/>
				</h1>
				<div className="c-pnp-header__actions">
					<menu className="c-pnp-header__nav">
						<ul className="c-pnp-header__list">
							<li className="c-pnp-header__item c-pnp-header__item--home">
								<button
									className={classNames('c-pnp-header__button', {
										['c-pnp-header__button--active']: activeButton === 'HOME',
									})}
									type="button"
									onClick={() =>
										checkDirtyForm({
											action() {
												navigate(ROUTES.HOME);
											},
										})
									}
								>
									<CTNIcon icon="home" color="none" className="c-pnp-header__button-icon" />
									<span>Inicio</span>
								</button>
							</li>
							<li className="c-pnp-header__item">
								<button
									className={classNames('c-pnp-header__button', {
										['c-pnp-header__button--active']: activeButton === 'MENSAJES',
									})}
									type="button"
									onClick={() =>
										checkDirtyForm({
											action() {
												navigate(ROUTES.MENSAJES);
											},
										})
									}
								>
									{numMessages > 0 && (
										<CTNBadge
											size="size-16"
											content={numMessages <= 99 ? String(numMessages) : '+99'}
											color="red"
											className="c-pnp-header__badge"
										/>
									)}
									<CTNIcon icon="chat" color="none" className="c-pnp-header__button-icon" />
									<span>Mensajes</span>
								</button>
							</li>
						</ul>
					</menu>
					<div className="c-pnp-header__item">
						{user && (
							<UserMini
								ref={useMiniRef}
								isReverse
								className="c-pnp-header__user"
								user={user}
								rol={rol}
								onClick={actionPopupRef?.current?.onToggleShow}
							/>
						)}
						<ActionPopup
							ref={actionPopupRef}
							refTrigger={useMiniRef}
							className="c-pnp-header__popup"
							items={ITEMS}
						/>
					</div>
				</div>
			</header>
		</div>
	);
}
